import { Component, inject, signal, output, input } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';

type SttStatus = 'idle' | 'recording' | 'processing' | 'success' | 'error';

interface SttResponse {
  result: boolean;
  message: string;
  data: string;
  obj: {
    original: string;
    corrected: string;
    language: string;
  };
  rowsAffected: number;
  log: string;
  errorLog: string;
  statusCode: number;
  statusText: string;
}

@Component({
  selector: 'app-stt',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule
],
  templateUrl: './stt.component.html',
  styleUrls: ['./stt.component.css']
})
export class SttComponent {
  private http = inject(HttpClient);

  status = signal<SttStatus>('idle');
  transcribedText = signal<string>('');
  originalText = signal<string>('');
  errorMessage = signal<string | null>(null);

  readonly transcribed = output<string>();
  readonly hideInput = input<boolean>(false);
  readonly sttUrl = input<string>('');
  readonly sttFormat = input<string>('');

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];

  get isRecording(): boolean {
    return this.status() === 'recording';
  }

  get isProcessing(): boolean {
    return this.status() === 'processing';
  }

  get hasError(): boolean {
    return this.status() === 'error';
  }

  get hasResult(): boolean {
    return this.status() === 'success' && this.transcribedText().length > 0;
  }

  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  private async startRecording(): Promise<void> {
    try {
      this.clearError();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.getMimeType()
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(100);
      this.status.set('recording');

    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.setError('請允許麥克風權限');
      } else if (error.name === 'NotFoundError') {
        this.setError('找不到音訊設備');
      } else {
        this.setError('無法開始錄音'+(error.message || ''));
      }
    }
  }

  private stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());

      // Wait for the last chunk and then transcribe
      this.mediaRecorder.onstop = () => this.transcribe();
    }

    this.status.set('processing');
  }

  private getMimeType(): string {
    const supportedMimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/m4a',
      'audio/wav'
    ];

    for (const mimeType of supportedMimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }

    throw new Error('此瀏覽器不支援 MediaRecorder');
  }

  private getFileExtension(mimeType: string): string {
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('mp4') || mimeType.includes('m4a')) return 'm4a';
    if (mimeType.includes('wav')) return 'wav';
    return 'webm';
  }

  private transcribe(): void {
    const mimeType = this.getMimeType();
    const audioBlob = new Blob(this.audioChunks, { type: mimeType });
    const formData = new FormData();
    formData.append('audio', audioBlob, `recording.${this.getFileExtension(mimeType)}`);

    this.status.set('processing');

    this.http.post<SttResponse>(this.sttUrl(), formData).subscribe({
      next: (response) => {
        if (response.result && response.obj?.corrected) {
          this.transcribedText.set(response.obj.corrected);
          this.originalText.set(response.obj.original || '');
          this.status.set('success');
          this.transcribed.emit(this.transcribedText());
        } else {
          this.setError(response.message || '辨識失敗，請重試');
        }
      },
      error: (error) => {
        if (!navigator.onLine) {
          this.setError('網路連線異常');
        } else if (error.status === 0) {
          this.setError('伺服器暫時無法使用，請稍後再試');
        } else {
          this.setError('辨識失敗，請重試');
        }
      }
    });
  }

  async copyToClipboard(): Promise<void> {
    const text = this.transcribedText();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      this.setError('複製失敗，請手動複製');
    }
  }

  private setError(message: string): void {
    this.errorMessage.set(message);
    this.status.set('error');
  }

  private clearError(): void {
    this.errorMessage.set(null);
  }

  retry(): void {
    this.clearError();
    this.status.set('idle');
  }
}
